import { Button } from '@/components/Button';
import { Chip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

/** HeaderBox props */
interface HeaderBoxProps {
  height?: string;
}

export const HeaderBox = styled('div')<HeaderBoxProps>(
  ({ height = '100%' }) => `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
  height: ${height};
  min-width: 0;
`
);

/** Principal container for modem and info */
export const ModemContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const ModemImage = styled('img')`
  width: 150px;
  height: auto;
`;

/** holds the default modem picture and fades in the model image on hover */
export const ModemImageWrapper = styled(Box)`
  position: relative;
  width: 150px;
  line-height: 0;

  &:hover > img:last-of-type {
    opacity: 1;
  }
`;

export const ModelImageHover = styled('img')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 24px;
  background-color: ${({ theme }) => theme.palette.fixed.white};
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
`;

export const RouterBox = styled(Box)`
  width: 100%;
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 8px;
`;

export const StatusBox = styled(Box)`
  font-weight: 500;
  font-size: 16px;
`;

export const TitleField = styled(Typography)`
  font-weight: 600;
  font-size: 11px;
  color: ${({ theme }) => theme.palette.grey['01']};
  white-space: nowrap;
  flex-shrink: 0;
`;

export const ValueField = styled('div')`
  font-weight: 400;
  font-size: 11px;
  color: ${({ theme }) => theme.palette.white['06']};
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: hidden;
`;

/** Expand button + quick actions: all inline on tablets, stacked below sm */
export const MobileActionsBox = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

/** Reboot / reset / map: always side by side, on their own line below sm */
export const QuickActionsRow = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const MobileExpandedButton = styled(Button)`
  width: 160px;
  height: 32px;
  border-color: ${({ theme }) => theme.palette.primary['01']};
  color: ${({ theme }) => theme.palette.primary['01']};
  background-color: ${({ theme }) => theme.palette.primary['01']};

  @media (max-width: 600px) {
    width: 100%;
  }
`;

/** Shared sizing for the icon-only quick action chips */
const quickActionChip = `
  max-width: 42px;
  display: flex;
  justify-content: center;
  align-items: center;

  /* Below sm the three chips split their own row evenly */
  @media (max-width: 600px) {
    max-width: none;
    width: auto;
    flex: 1 1 0;
    height: 32px;
  }
`;

export const RebootButton = styled(Chip)`
  ${quickActionChip}
  background-color: ${({ theme }) => theme.palette.secondary['01']};
  color: ${({ theme }) => theme.palette.fixed.black};

  span {
    display: flex;
    height: 20px;
  }
`;

export const ResetButton = styled(Chip)`
  ${quickActionChip}
  background-color: ${({ theme }) => theme.palette.status.offline};
  color: ${({ theme }) => theme.palette.fixed.white};

  span {
    display: flex;
    height: 20px;
  }
`;

export const MapButton = styled(Chip)`
  ${quickActionChip}
  background-color: ${({ theme }) => theme.palette.primary['01'] + '70'};
  color: ${({ theme }) => theme.palette.primary['01']};

  span {
    display: flex;
  }
`;

export const UpdateButton = styled('div')`
  background-color: ${({ theme }) => theme.palette.primary['01']};
  height: 22px;
  width: 22px;
  flex-shrink: 0;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.fixed.white};

  &:hover {
    color: ${({ theme }) => theme.palette.primary['06']};
    background-color: ${({ theme }) => theme.palette.primary['02']};
  }
`;

export const GeneralViewBox = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  align-content: center;
  gap: 8px;
  margin-bottom: 16px;
  min-width: 0;

  /* Model name truncates instead of pushing the action buttons out of the card */
  & > .MuiTypography-root {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** Container for model info */
export const ModelInfoSection = styled('div')`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  min-width: 0;
`;

/** IconWithMargin Interface */
interface IconWithMarginProps {
  margin?: string;
}

export const IconWithMargin = styled('div')<IconWithMarginProps>(
  ({ margin = '12px' }) => `
  display: flex;
  margin-right: ${margin};
`
);

/** Toggle stretched to its parent */
const toggleFullWidth = `
  width: 100%;
  align-self: stretch;
`;

/** ToggleButtonGroupContainer props */
interface ToggleButtonGroupContainerProps {
  /** Match the width of sibling controls on every screen size */
  fullWidth?: boolean;
}

/** Container for togglebuttongroup with specific styles */
export const ToggleButtonGroupContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<ToggleButtonGroupContainerProps>(
  ({ fullWidth }) => `
  height: 36px;
  width: auto;
  /* Roughly the width of the large-screen controls column; never wider than the card */
  min-width: min(300px, 100%);
  align-self: flex-start;

  /* Group fills the container, options split it evenly */
  .MuiToggleButtonGroup-root {
    width: 100%;
  }

  .MuiToggleButton-root {
    flex: 1 1 0;
    gap: 8px;
  }

  ${fullWidth ? toggleFullWidth : ''}

  /* Below sm the toggle always spans the card */
  @media (max-width: 700px) {
    ${toggleFullWidth}
  }
`
);

/** Interface for ControlsStack - multiples props for media queries */
interface ControlsStackProps {
  direction?: 'row' | 'column';
  gap?: string;
}

export const ControlsStack = styled('div')<ControlsStackProps>(
  ({ direction = 'column', gap = '24px' }) => `
  display: flex;
  flex-direction: ${direction};
  gap: ${gap};
`
);

export const ButtonsRow = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
`;
